import { cn } from "@/styles/utils"
import type { Task } from "@taskfortress/core/src/clientData/contentTypes"
import { getTodaysSampleTask } from "@taskfortress/core/src/utils"
import React, { useCallback, useMemo } from "react"
import { useData } from "../../../providers/DataProvider"
import { DesktopOnly } from "../../DesktopOnly"
import { MobileOnly } from "../../MobileOnly"
import { Modal } from "../../Modal"
import { TaskForm } from "./TaskForm"
import { TaskModalDesktop } from "./TaskModalDesktop"
import { TaskModalMobile } from "./TaskModalMobile"

interface TaskModalProps {
  task: Task | null
  onClose: () => void
  onSave: (task: Task) => void
  onDelete?: (task: Task) => void
  isVisible: boolean
}

export const TaskModal: React.FC<TaskModalProps> = ({
  task,
  onClose,
  onSave,
  onDelete,
  isVisible,
}) => {
  const { tags } = useData()

  // Use useMemo for derived values
  const tagSuggestions = useMemo(() => tags.map((tag) => tag.name), [tags])
  const saveButtonText = useMemo(
    () => (task?.id ? "Save" : "Create"),
    [task?.id],
  )

  // Wrap onSave to call onClose after saving
  const handleSave = useCallback(
    (updatedTask: Task) => {
      onSave(updatedTask)
      onClose()
    },
    [onSave, onClose],
  )

  // Use useMemo for key generation
  const taskKey = useMemo(() => {
    if (task?.id) {
      // For existing tasks, include updatedAt to ensure remounting when task data changes
      return `${task.id}-${task.updatedAt || ""}`
    }
    // For new tasks, create a key based on initial values to ensure remounting when they change
    const initialValuesKey = JSON.stringify({
      title: task?.title || "",
      description: task?.description || "",
      tags: task?.tags || [],
      dueDate: task?.dueDate,
      status: task?.status,
    })
    // Create a simple hash from the stringified values
    const hash = initialValuesKey
      .split("")
      .reduce((acc, char) => acc + char.charCodeAt(0), 0)
    return `new-task-${hash}`
  }, [
    task?.id,
    task?.updatedAt,
    task?.title,
    task?.description,
    task?.tags,
    task?.dueDate,
    task?.status,
  ])

  if (!task) return null

  const sampleTask = getTodaysSampleTask()

  const handleDeleteTask = () => {
    onDelete?.(task)
    onClose()
  }

  return (
    <TaskForm
      key={taskKey}
      task={task}
      onSave={handleSave}
      onClose={onClose}
      tagSuggestions={tagSuggestions}
    >
      {({
        titleField,
        descriptionField,
        statusField,
        dueDateField,
        tagsField,
        form,
        handleFormSubmit,
      }) => {
        // Wrap onClose to reset form first
        const handleClose = () => {
          form.reset()
          onClose()
        }

        return (
          <>
            <MobileOnly>
              <Modal
                isVisible={isVisible}
                onClose={handleClose}
                size="full"
                contentClassName={cn(
                  "bg-slate-100",
                  statusField.value && "bg-slate-300",
                )}
              >
                <TaskModalMobile
                  titleField={titleField}
                  descriptionField={descriptionField}
                  statusField={statusField}
                  dueDateField={dueDateField}
                  tagsField={tagsField}
                  form={form}
                  handleFormSubmit={handleFormSubmit}
                  handleDeleteTask={handleDeleteTask}
                  saveButtonText={saveButtonText}
                  sampleTask={sampleTask}
                  tagSuggestions={tagSuggestions}
                  task={task}
                  onDelete={onDelete}
                  isVisible={isVisible}
                />
              </Modal>
            </MobileOnly>

            <DesktopOnly>
              <Modal
                isVisible={isVisible}
                onClose={handleClose}
                size="xl"
                contentClassName={cn(
                  "bg-slate-100",
                  statusField.value && "bg-slate-300",
                )}
                bodyClassName="min-h-[90%]"
              >
                <TaskModalDesktop
                  titleField={titleField}
                  descriptionField={descriptionField}
                  statusField={statusField}
                  dueDateField={dueDateField}
                  tagsField={tagsField}
                  form={form}
                  handleFormSubmit={handleFormSubmit}
                  handleDeleteTask={handleDeleteTask}
                  saveButtonText={saveButtonText}
                  sampleTask={sampleTask}
                  tagSuggestions={tagSuggestions}
                  task={task}
                  onDelete={onDelete}
                  isVisible={isVisible}
                />
              </Modal>
            </DesktopOnly>
          </>
        )
      }}
    </TaskForm>
  )
}
